import { Consumer, Subscription } from '@rails/actioncable';
import { skipToken, useQuery, useQueryClient } from '@tanstack/react-query';
import { Rutter_ItemRule_Read } from '@treadinc/horizon-api-spec';
import { useEffect } from 'react';

import { RutterItemRule } from '~hooks/useRutter/models';
import useRutter from '~hooks/useRutter/useRutter';
import { realTimeChannels } from '~services/realTimeChannels';
import { Nullable } from '~types/Nullable';
import { channelMessageData, subscribeToChannel } from '~utils/rtu/rtu-utils';

interface UseRutterItemRulesQueryProps {
  connectionId: Nullable<string>;
}

const rutterItemRulesQueryKey = (connectionId: Nullable<string>) => [
  'rutterItemRules',
  connectionId,
];

export default function useRutterItemRulesQuery({
  connectionId,
}: UseRutterItemRulesQueryProps) {
  const { getItemRules } = useRutter();
  const queryClient = useQueryClient();
  const queryKey = rutterItemRulesQueryKey(connectionId);

  // Every rule save, including a finished draft, is broadcast with the full rule.
  useEffect(() => {
    if (!connectionId) {
      return;
    }

    let subscription: Subscription<Consumer> | undefined;

    subscribeToChannel<{ data: Rutter_ItemRule_Read }>(
      realTimeChannels.RutterItemRuleChannel,
      null,
      (createdSubscription) => {
        subscription = createdSubscription;
      },
      (message) => {
        const data = channelMessageData<Rutter_ItemRule_Read>(message);

        if (!data) {
          return;
        }

        const updatedRule = RutterItemRule.parse(data);

        queryClient.setQueryData<RutterItemRule[]>(
          rutterItemRulesQueryKey(connectionId),
          (rules = []) =>
            rules.some((rule) => rule.id === updatedRule.id)
              ? rules.map((rule) => (rule.id === updatedRule.id ? updatedRule : rule))
              : [...rules, updatedRule],
        );
      },
      { rutter_connection_id: connectionId },
    );

    // an existing subscription is reused per channel, so a new connection needs a fresh one
    return () => {
      subscription?.unsubscribe();
    };
  }, [connectionId, queryClient]);

  return useQuery({
    queryKey,
    queryFn: connectionId
      ? ({ signal }) => getItemRules(connectionId, signal)
      : skipToken,
  });
}
